import { Suspense, lazy, useEffect, useState } from "react";
import { NavLink, Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert, AppBar, Avatar, Badge, Box, Button, Chip, Divider, Drawer, IconButton, List, ListItemButton,
  ListItem, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Toolbar,
  Tooltip, Typography, alpha, useMediaQuery,
} from "@mui/material";
import { useAuth } from "@/auth/AuthContext";
import { useColorMode } from "@/brand/ColorModeContext";
import { BrandLockup, Wordmark } from "@/brand/Logo";
import { fetcher } from "@/lib/query";
import { headerWash } from "@/theme";
import type { Theme } from "@mui/material";
import type { FluentIcon } from "@/design/icons/createIcon";
import TourRunner from "@/components/tour/TourRunner";
import WorkingInsideBanner from "@/features/privacy/WorkingInsideBanner";
import { AlertIcon } from "../design/icons/AlertIcon";
import { ChatIcon } from "../design/icons/ChatIcon";
import { ShieldPersonIcon } from "../design/icons/ShieldPersonIcon";
import { BeakerIcon } from "../design/icons/BeakerIcon";
import { BoardIcon } from "../design/icons/BoardIcon";
import { BoxIcon } from "../design/icons/BoxIcon";
import { BrainCircuitIcon } from "../design/icons/BrainCircuitIcon";
import { CloudIcon } from "../design/icons/CloudIcon";
import { DataHistogramIcon } from "../design/icons/DataHistogramIcon";
import { DocumentTextIcon } from "../design/icons/DocumentTextIcon";
import { FolderIcon } from "../design/icons/FolderIcon";
import { HatGraduationIcon } from "../design/icons/HatGraduationIcon";
import { HeadsetIcon } from "../design/icons/HeadsetIcon";
import { HeartPulseIcon } from "../design/icons/HeartPulseIcon";
import { HistoryIcon } from "../design/icons/HistoryIcon";
import { KeyIcon } from "../design/icons/KeyIcon";
import { LibraryIcon } from "../design/icons/LibraryIcon";
import { NavigationIcon } from "../design/icons/NavigationIcon";
import { PeopleCommunityIcon } from "../design/icons/PeopleCommunityIcon";
import { QuestionCircleIcon } from "../design/icons/QuestionCircleIcon";
import { RocketIcon } from "../design/icons/RocketIcon";
import { ScanIcon } from "../design/icons/ScanIcon";
import { ServerIcon } from "../design/icons/ServerIcon";
import { SettingsIcon } from "../design/icons/SettingsIcon";
import { ShieldIcon } from "../design/icons/ShieldIcon";
import { ShieldTaskIcon } from "../design/icons/ShieldTaskIcon";
import { SignOutIcon } from "../design/icons/SignOutIcon";
import { SparkleIcon } from "../design/icons/SparkleIcon";
import { TextBulletListLtrIcon } from "../design/icons/TextBulletListLtrIcon";
import { TrophyIcon } from "../design/icons/TrophyIcon";
import { WeatherMoonIcon } from "../design/icons/WeatherMoonIcon";
import { WeatherSunnyIcon } from "../design/icons/WeatherSunnyIcon";
import { BuildingShopIcon } from "@/design/icons/BuildingShopIcon";
import { LockClosedKeyIcon } from "@/design/icons/LockClosedKeyIcon";

// The Operator beside every page (MultiDoc staff it is turned on for); loaded on its own.
const OperatorDock = lazy(() => import("@/components/agent/OperatorDock"));

const WIDTH = 248;
/** Wide enough for an icon and its focus ring, and nothing else. */
const MINI = 64;
const COLLAPSED_KEY = "mdc.nav.collapsed";

/** Whether the navigation is collapsed, remembered per browser. */
function useCollapsedNav() {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
  });
  const toggle = () => setCollapsed((was) => {
    const now = !was;
    try { localStorage.setItem(COLLAPSED_KEY, now ? "1" : "0"); } catch { /* private window */ }
    return now;
  });
  return { collapsed, toggle };
}

interface NavItem {
  to: string;
  label: string;
  /** Used instead of `label` when the signed-in org is a partner. */
  partnerLabel?: string;
  /** Fluent icon component: drawn filled when the item is the current page. */
  icon: FluentIcon;
  perms: string[];
  end?: boolean;
  /** Shows the count of unseen support notifications on the icon. */
  unseenBadge?: boolean;
  /** The Operator's inbox: routine reports and changes awaiting approval. */
  inboxBadge?: boolean;
  /** Only for a partner's own people: MultiDoc staff reach it per partner. */
  partnerOnly?: boolean;
  /** MultiDoc's own tooling; a partner viewer holding the permission is not shown it. */
  vendorOnly?: boolean;
  /** Needs the permission granted by name; the super-admin wildcard does not count. */
  explicit?: boolean;
}

/** The `data-tour` anchor for a menu entry: `/support/tickets` → `nav-support-tickets`. */
const navTour = (to: string) => `nav-${to.replace(/^\/+/, "").replace(/\//g, "-") || "overview"}`;

/**
 * Nav is permission-driven and grouped by what somebody is doing, not by which
 * backend module serves it: a partner sees two or three sections, a vendor
 * admin sees six, and neither is shown a link that would 403.
 */
const SECTIONS: { heading: string | null; items: NavItem[] }[] = [
  {
    heading: null,
    items: [
      { to: "/", label: "Overview", icon: BoardIcon, perms: [], end: true },
      // A general chat with every AI tool: the web, images, diagrams, files.
      { to: "/assistant", label: "Assistant", icon: ChatIcon, perms: [], inboxBadge: true },
      // Courses and on-screen walkthroughs; near the top so a new partner finds it.
      { to: "/training", label: "Training", icon: HatGraduationIcon, perms: ["training.read"] },
      { to: "/certification", label: "Certification", icon: TrophyIcon, perms: ["cert.learn"] },
    ],
  },
  {
    heading: "Deployments",
    items: [
      // Two modules over one engine: customers' live systems, and demos for prospects.
      { to: "/production", label: "Production", partnerLabel: "Customers", icon: CloudIcon,
        perms: ["production.read"] },
      { to: "/demos", label: "Demos", icon: BeakerIcon, perms: ["demos.read"] },
      { to: "/trials", label: "Trials", icon: ScanIcon, perms: ["trials.read"] },
      { to: "/jobs", label: "Activity", icon: TextBulletListLtrIcon, perms: ["jobs.read"] },
      { to: "/packs", label: "Demo packs", icon: BoxIcon, perms: ["packs.read"] },
      { to: "/clusters", label: "Clusters", icon: ServerIcon, perms: ["clusters.read"] },
    ],
  },
  {
    heading: "Product",
    items: [
      { to: "/licenses", label: "Licences", icon: KeyIcon, perms: ["licenses.read"] },
      { to: "/releases", label: "Releases", icon: RocketIcon, perms: ["releases.read"], vendorOnly: true },
      { to: "/marketplace", label: "Marketplace", icon: BuildingShopIcon, perms: ["releases.read"],
        vendorOnly: true },
      { to: "/install-profiles", label: "Install profiles", icon: BoxIcon, perms: ["releases.read"],
        vendorOnly: true },
      { to: "/fleet", label: "Fleet", icon: HeartPulseIcon, perms: ["fleet.read"] },
    ],
  },
  {
    // ECM Cloud, the multi-tenant SaaS (backend console/cloud). MultiDoc staff only.
    heading: "Cloud",
    items: [
      { to: "/cloud/tenants", label: "Tenants", icon: CloudIcon, perms: ["cloud.read"],
        vendorOnly: true },
      { to: "/cloud/pricing", label: "Plans & Pricing", icon: BuildingShopIcon,
        perms: ["cloud.read"], vendorOnly: true },
    ],
  },
  {
    heading: "Support",
    items: [
      { to: "/support/tickets", label: "Tickets", icon: HeadsetIcon, perms: ["support.read"] },
      { to: "/support/articles", label: "Help articles", icon: QuestionCircleIcon, perms: ["support.read"] },
      { to: "/support/notifications", label: "Notifications", icon: AlertIcon,
        perms: ["support.read"], unseenBadge: true },
      { to: "/support/compliance", label: "Compliance", icon: ShieldTaskIcon, perms: ["support.read"] },
    ],
  },
  {
    heading: "Go to market",
    items: [
      { to: "/sales", label: "Opportunities", icon: SparkleIcon, perms: ["sales.read"] },
      // Files are kept per project, so they sit with the projects.
      { to: "/files", label: "Files", icon: FolderIcon, perms: ["files.read", "sales.read"] },
      // ADR 0011: the CRM is the vendor's pipeline across many ECM tenants —
      // accounts, contacts, deals, kanban. Lives at /crm and is gated by
      // ``crm.account.view``; partner roles without the permission won't see it.
      { to: "/crm", label: "CRM", icon: BoardIcon, perms: ["crm.account.view"] },
      { to: "/reports", label: "Reports", icon: DataHistogramIcon, perms: ["reports.read"] },
      { to: "/templates", label: "Templates", icon: DocumentTextIcon, perms: ["files.read"] },
      // A partner holds `resellers.read` so it can see itself, and the handler
      // scopes it to exactly that — so for them the link is their own
      // organisation, not a directory of everybody else's.
      { to: "/partners", label: "Partners", partnerLabel: "My organisation",
        icon: PeopleCommunityIcon, perms: ["resellers.read"] },
      // What MultiDoc staff may open, and the log of what they did.
      { to: "/privacy", label: "Privacy & access", icon: ShieldIcon,
        perms: ["access.log.read"], partnerOnly: true },
    ],
  },
  {
    heading: "Settings",
    items: [
      { to: "/kb", label: "Knowledge base", icon: LibraryIcon, perms: ["kb.read"] },
      { to: "/settings/ai", label: "AI", icon: BrainCircuitIcon, perms: ["ai.admin"] },
      { to: "/settings/security", label: "Encryption & keys", icon: LockClosedKeyIcon, perms: ["keys.manage"],
        explicit: true },
      { to: "/admin", label: "Administration", icon: SettingsIcon, perms: ["iam.users.manage"] },
      { to: "/audit", label: "Audit log", icon: HistoryIcon, perms: ["audit.read"] },
    ],
  },
];

/**
 * A partner's menu: the same entries, grouped by the job a reseller's sales team
 * does (bid, demo, get help) and without MultiDoc's operating tools. Activity,
 * Demo packs, Fleet, Compliance and Templates are left out of the
 * menu only; their routes stay, so a link from inside a page still works.
 * Licences shows only to whoever can issue them. Administration is left out too:
 * a partner admin invites and manages colleagues under My organisation → Users,
 * which is what the training teaches, and two places for one job confused people.
 */
const PARTNER_SECTIONS: { heading: string | null; items: { to: string; perms?: string[] }[] }[] = [
  { heading: null, items: [{ to: "/" }, { to: "/assistant" }, { to: "/training" },
                           { to: "/certification" }] },
  { heading: "Sell", items: [{ to: "/sales" }, { to: "/crm" }, { to: "/files" }, { to: "/kb" },
                             { to: "/reports" }] },
  { heading: "Demos & customers", items: [
    { to: "/demos" }, { to: "/trials" }, { to: "/production" },
    { to: "/licenses", perms: ["licenses.read", "licenses.issue"] }] },
  { heading: "Help", items: [{ to: "/support/tickets" }, { to: "/support/articles" },
                             { to: "/support/notifications" }] },
  { heading: "Organisation", items: [{ to: "/partners" }, { to: "/privacy" }] },
];

function partnerSections(): { heading: string | null; items: NavItem[] }[] {
  const byPath = new Map(SECTIONS.flatMap((section) => section.items).map((item) => [item.to, item]));
  return PARTNER_SECTIONS.map((section) => ({
    heading: section.heading,
    items: section.items.flatMap(({ to, perms }) => {
      const item = byPath.get(to);
      if (!item) return [];
      return [{ ...item, perms: perms ?? item.perms }];
    }),
  }));
}

/** Header text at a given strength, and a fill that moves away from it, so
 *  chips and buttons on the brand-coloured header keep their contrast. */
const ink = (a = 1) => (t: Theme) => alpha(t.palette.header.contrastText, a);
const wash = (a: number) => (t: Theme) => headerWash(t, a);

const ORG_LABEL: Record<string, string> = {
  vendor: "MultiDoc",
  reseller: "Partner",
  customer: "Customer",
};

function initials(name: string, email: string): string {
  const source = name.trim() || email.split("@")[0].replace(/[._-]+/g, " ");
  const parts = source.split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]!.toUpperCase()).join("") || "?";
}

export default function AppShell() {
  const { me, logout, can } = useAuth();
  const { mode, toggle } = useColorMode();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  // The nav badge: one row and a count, once a minute.
  const feed = useQuery({
    ...fetcher<{ unseen: number }>("/support/notifications?unseen_only=true&limit=1"),
    enabled: can("support.read"),
    refetchInterval: 60_000,
  });
  const unseen = feed.data?.unseen ?? 0;

  const sections = (me?.org.kind === "reseller" ? partnerSections() : SECTIONS)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => (item.perms.length === 0 || can(...item.perms))
                                             && !(item.partnerOnly && me?.org.kind === "vendor")
                                             && !(item.vendorOnly && me?.org.kind !== "vendor")
                                             && !(item.explicit && !item.perms.every((p) => me?.permissions.includes(p)))),
    }))
    .filter((section) => section.items.length > 0);

  const orgKind = me?.org.kind ?? "customer";
  const isPartner = orgKind === "reseller";
  const { collapsed: wideCollapsed, toggle: toggleWide } = useCollapsedNav();
  const { pathname } = useLocation();
  // On a phone or a narrow window the menu is a drawer over the page, closed until
  // the menu button opens it, and closed again by choosing a page.
  const narrow = useMediaQuery((t: Theme) => t.breakpoints.down("md"));
  const [phoneOpen, setPhoneOpen] = useState(false);
  useEffect(() => { setPhoneOpen(false); }, [pathname]);
  const collapsed = narrow ? false : wideCollapsed;
  const toggleNav = narrow ? () => setPhoneOpen((v) => !v) : toggleWide;
  const navWidth = collapsed ? MINI : WIDTH;
  const menuShown = narrow ? phoneOpen : !wideCollapsed;
  const inbox = useQuery({
    ...fetcher<{ unread: number; approvals: number }>("/assistant/inbox"),
    enabled: me?.org.kind === "vendor" && can("assistant.operate"),
    refetchInterval: 60_000,
  });
  const inboxCount = (inbox.data?.unread ?? 0) + (inbox.data?.approvals ?? 0);
  const isActive = (item: NavItem) => !!matchPath({ path: item.to, end: !!item.end }, pathname);

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar position="fixed" data-chrome="header" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }} elevation={0}>
        <Toolbar sx={{ minHeight: 56, gap: 1 }}>
          <Tooltip title={menuShown ? "Hide the menu" : "Show the menu"}>
            <IconButton size="small" onClick={toggleNav} sx={{ color: "inherit", mr: 0.5, "&:hover": { bgcolor: wash(0.15) } }}
                        aria-label={menuShown ? "Hide the menu" : "Show the menu"}
                        aria-expanded={narrow ? phoneOpen : undefined}>
              <NavigationIcon fontSize="small"
                            sx={{ transform: menuShown ? "none" : "rotate(180deg)",
                                  transition: "transform 150ms" }} />
            </IconButton>
          </Tooltip>
          {collapsed ? null : (
            <Box sx={{ width: { md: WIDTH - 72 }, flexShrink: { xs: 1, md: 0 }, minWidth: 0,
                       overflow: "hidden" }}>
              {/* A phone has room for the wordmark and nothing after it. */}
              {narrow ? <Wordmark onBrand height={22} /> : <BrandLockup onBrand height={22} />}
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Whose Console this is. A partner is always looking at their own
              organisation's data, so naming it is the difference between
              trusting a licence list and wondering whose it is. */}
          <Chip
            size="small"
            data-tour="topbar-org"
            label={me?.org.name || ORG_LABEL[orgKind]}
            sx={{ "&.MuiChip-filled": { bgcolor: wash(0.18), color: ink() }, "& .MuiChip-label": { px: 1.25 } }}
          />
          {isPartner && (
            <Chip
              size="small"
              variant="outlined"
              label="Partner"
              sx={{ color: ink(), borderColor: ink(0.6) }}
            />
          )}

          <Tooltip title={mode === "dark" ? "Switch to light" : "Switch to dark"}>
            <IconButton color="inherit" size="small" onClick={toggle} sx={{ ml: 0.5, "&:hover": { bgcolor: wash(0.15) } }}
                        aria-label={mode === "dark" ? "Switch to light" : "Switch to dark"}>
              {mode === "dark" ? <WeatherSunnyIcon fontSize="small" /> : <WeatherMoonIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title={me?.user.email ?? ""}>
            <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} sx={{ ml: 0.5 }}
                        data-tour="topbar-avatar">
              <Avatar sx={{ width: 30, height: 30, bgcolor: wash(0.25), color: ink(), fontSize: "0.8333rem" }}>
                {initials(me?.user.name ?? "", me?.user.email ?? "")}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchor}
            open={!!anchor}
            onClose={() => setAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 240, mt: 1 } } }}
          >
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography variant="subtitle2" noWrap>
                {me?.user.name || me?.user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {me?.user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {ORG_LABEL[orgKind]} · {me?.org.name}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setAnchor(null); navigate("/profile"); }}>
              <ListItemIcon><ShieldPersonIcon fontSize="small" /></ListItemIcon>
              Your account &amp; two-factor
            </MenuItem>
            <MenuItem onClick={() => void logout()}>
              <ListItemIcon><SignOutIcon fontSize="small" /></ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={narrow ? "temporary" : "permanent"}
        open={narrow ? phoneOpen : true}
        onClose={() => setPhoneOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: narrow ? 0 : navWidth,
          flexShrink: 0,
          transition: (t) => t.transitions.create("width", { duration: 180 }),
          "& .MuiDrawer-paper": {
            width: navWidth,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            transition: (t) => t.transitions.create("width", { duration: 180 }),
          },
        }}
      >
        <Toolbar sx={{ minHeight: 56 }} />
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.25, pb: 2 }}>
          {sections.map((section, index) => (
            <List
              key={section.heading ?? `section-${index}`}
              dense
              sx={{ pt: index === 0 ? 1.5 : 0.5 }}
              subheader={
                section.heading && !collapsed ? (
                  <ListSubheader
                    disableSticky
                    sx={{ bgcolor: "transparent", color: "text.secondary", typography: "overline", lineHeight: "28px", px: 1.5 }}
                  >
                    {section.heading}
                  </ListSubheader>
                ) : undefined
              }
            >
              {section.items.map((item) => (
                // Collapsed, the icon is all that is left of the name. ListItem
                // provides the <li>, so the <ul> holds list items (axe: list).
                <ListItem key={item.to} disablePadding sx={{ display: "block" }}>
                <Tooltip placement="right"
                         title={collapsed ? (isPartner && item.partnerLabel) || item.label : ""}>
                <ListItemButton
                  data-tour={navTour(item.to)}
                  component={NavLink}
                  to={isPartner && item.to === "/partners" && me?.org.id
                    ? `/partners/${me.org.id}` : item.to}
                  end={item.end}
                  sx={{
                    minHeight: 36,
                    px: 1.5,
                    ...(collapsed
                      ? { justifyContent: "center",
                          "& .MuiListItemIcon-root": { minWidth: 0, justifyContent: "center" } }
                      : {}),
                    mb: 0.25,
                    color: "nav.muted",
                    "& .MuiListItemIcon-root": { color: "inherit", minWidth: 32 },
                    "&:hover": { bgcolor: (t) => alpha(t.palette.nav.contrastText, 0.05), color: "nav.contrastText" },
                    // MultiDoc nav pane: the current page is a raised white tile
                    // with a brand bar on its left edge and a filled icon.
                    "&.active": {
                      position: "relative",
                      bgcolor: "nav.selected",
                      color: "text.primary",
                      boxShadow: 1,
                      "&:hover": { bgcolor: "nav.selected" },
                      "&::before": {
                        content: '""', position: "absolute", left: 0, top: "25%", bottom: "25%",
                        width: 3, borderRadius: 2, bgcolor: "primary.main",
                      },
                      "& .MuiListItemIcon-root": { color: "primary.main" },
                      "& .MuiListItemText-primary": { fontWeight: 600 },
                    },
                  }}
                >
                  <ListItemIcon>
                    {item.inboxBadge && inboxCount ? (
                      <Badge badgeContent={inboxCount} color="warning" max={99}>
                        <item.icon size={20} filled={isActive(item)} />
                      </Badge>
                    ) : item.unseenBadge && unseen ? (
                      <Badge badgeContent={unseen} color="error" max={99}>
                        <item.icon size={20} filled={isActive(item)} />
                      </Badge>
                    ) : <item.icon size={20} filled={isActive(item)} />}
                  </ListItemIcon>
                  {collapsed ? null : (
                    <ListItemText
                      primary={(isPartner && item.partnerLabel) || item.label}
                      slotProps={{ primary: { fontSize: "0.9722rem", noWrap: true } }}
                    />
                  )}
                </ListItemButton>
                </Tooltip>
                </ListItem>
              ))}
            </List>
          ))}
        </Box>

      </Drawer>

      <Box
        component="main"
        sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, minHeight: "100vh", bgcolor: "background.default",
              minWidth: 0,
              // A <Page fill> (the Assistant) fits the window: no page scroll, so
              // the chat stays put whatever banners sit above it.
              "&:has([data-fill-page])": {
                display: { md: "flex" }, flexDirection: "column", height: { md: "100vh" },
              } }}
      >
        <Toolbar sx={{ minHeight: 56 }} />
        <WorkingInsideBanner />
        {me?.staff_mfa_required && !me.staff_mfa_setup_due && me.staff_mfa_required_from ? (
          <Alert severity="warning" sx={{ mb: 2 }}
                 action={<Button color="inherit" size="small" onClick={() => navigate("/profile")}>Set it up</Button>}>
            Two-step verification is required for MultiDoc staff from{" "}
            {new Date(me.staff_mfa_required_from).toLocaleDateString(undefined, { day: "numeric", month: "long" })}.
            Set it up now on your profile — it takes a minute with an authenticator app.
          </Alert>
        ) : null}
        <Outlet />
        <TourRunner />
        {me?.org.kind === "vendor" ? (
          <Suspense fallback={null}><OperatorDock /></Suspense>
        ) : null}
      </Box>
    </Box>
  );
}
